import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import api from '../../services/api';

const WizardContainer = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 30px;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : 'white'};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme?.mode === 'dark' ? 'none' : '0 4px 20px rgba(0,0,0,0.08)'};
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'transparent'};
`;

const WizardHeader = styled.div`
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : '#2d3748'};
  margin-bottom: 10px;
`;

const Steps = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 15px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#e2e8f0'};
    z-index: 0;
  }
`;

const Step = styled.div`
  position: relative;
  z-index: 1;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : 'white'};
  padding: 0 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.active ? '#667eea' : props.completed ? '#48bb78' : props.theme?.mode === 'dark' ? '#374151' : '#e2e8f0'};
  color: ${props => props.active || props.completed ? 'white' : props.theme?.mode === 'dark' ? '#9ca3af' : '#718096'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: all 0.3s;
`;

const StepLabel = styled.span`
  font-size: 12px;
  color: ${props => props.active ? '#667eea' : props.theme?.mode === 'dark' ? '#9ca3af' : '#718096'};
  font-weight: ${props => props.active ? '600' : '400'};
`;

const FormSection = styled.div`
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${({ theme }) => theme?.mode === 'dark' ? '#e5e7eb' : '#4a5568'};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.2s;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'white'};
  color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : 'inherit'};

  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : '#a0aec0'};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'white'};
  color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : 'inherit'};

  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : '#a0aec0'};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4); }
  ` : `
    background: ${props.theme?.mode === 'dark' ? '#374151' : '#edf2f7'};
    color: ${props.theme?.mode === 'dark' ? '#e5e7eb' : '#4a5568'};
    &:hover { background: ${props.theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'}; }
  `}
`;

const AIResponseCard = styled.div`
  background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#f7fafc'};
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #667eea;
    background: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : 'white'};
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }

  ${props => props.selected && `
    border-color: #667eea;
    background: ${props.theme?.mode === 'dark' ? '#1f2937' : 'white'};
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  `}
`;

const CampaignWizard = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const theme = { mode: isDark ? 'dark' : 'light' };
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        objective: '',
        audience: '',
        budget: '',
        timeline: '',
        selectedStrategy: null
    });

    const [aiSuggestions, setAiSuggestions] = useState(null);

    const steps = [
        { number: 1, label: 'Objective' },
        { number: 2, label: 'AI Strategy' },
        { number: 3, label: 'Review' },
        { number: 4, label: 'Launch' }
    ];

    const handleNext = async () => {
        if (step === 1) {
            setLoading(true);
            setLoading(true);
            try {
                const response = await api.post('/formal-crew/workflows', {
                    workflow_type: 'campaign_strategy',
                    objective: formData.objective,
                    context: {
                        audience: formData.audience,
                        budget: formData.budget,
                        timeline: formData.timeline
                    }
                });

                if (response.data.success) {
                    // Poll for results since the workflow is async
                    // For this implementation, we'll assume the agent runs quickly enough or we'd implement a polling mechanism
                    // But wait, the current implementation of run_async_ai_task in formal_crew.py seems to await the result?
                    // Let's check formal_crew.py again. It calls run_async_ai_task(execute_formal_crew_workflow(...))
                    // execute_formal_crew_workflow is async. run_async_ai_task runs it in a loop.
                    // So the response should contain the result if it finishes within the timeout.

                    const result = response.data.result;

                    if (result && result.results && result.results.campaign_strategy) {
                        const strategy = result.results.campaign_strategy;

                        // Map the AI strategy to our UI format
                        // The AI returns a single comprehensive strategy, but our UI expects options.
                        // We can either adapt the UI to show one detailed strategy, or ask the AI for options.
                        // For now, let's wrap the single strategy into an array or parse if it returned options.

                        let suggestions = [];

                        if (Array.isArray(strategy.options)) {
                            suggestions = strategy.options;
                        } else {
                            // If the AI returned a single strategy object, wrap it
                            suggestions = [{
                                id: 1,
                                name: strategy.campaign_name || 'AI Recommended Strategy',
                                description: strategy.executive_summary || strategy.description || 'Comprehensive strategy based on your inputs.',
                                channels: strategy.channels || strategy.marketing_channels || [],
                                estimated_roi: strategy.estimated_roi || strategy.roi_estimate || 'High'
                            }];
                        }

                        setAiSuggestions(suggestions);
                        setStep(2);
                    } else {
                        // Fallback if structure is unexpected but call succeeded
                        console.warn("Unexpected API response structure:", result);
                        setAiSuggestions([{
                            id: 1,
                            name: 'AI Strategy Generated',
                            description: 'Strategy generated but format requires adjustment. Check console for details.',
                            channels: ['Multi-channel'],
                            estimated_roi: 'TBD'
                        }]);
                        setStep(2);
                    }
                }
            } catch (error) {
                console.error("Error generating strategy:", error);
                alert("Failed to generate strategy. Please try again.");
            } finally {
                setLoading(false);
            }
        } else if (step === 2) {
            if (!formData.selectedStrategy) {
                alert('Please select a strategy');
                return;
            }
            setStep(3);
        } else if (step === 3) {
            setLoading(true);
            // TODO: Submit final campaign
            setTimeout(() => {
                setLoading(false);
                setStep(4);
            }, 1500);
        } else {
            navigate('/ai-command-center');
        }
    };

    const handleBack = () => {
        setStep(step - 1);
    };

    return (
        <ThemeProvider theme={theme}>
            <WizardContainer>
                <WizardHeader>
                    <Title>🚀 New Campaign Wizard</Title>
                    <p style={{ color: isDark ? '#9ca3af' : '#718096' }}>Let our AI agents design your perfect campaign</p>
                </WizardHeader>

            <Steps>
                {steps.map(s => (
                    <Step key={s.number}>
                        <StepCircle
                            active={step === s.number}
                            completed={step > s.number}
                        >
                            {step > s.number ? '✓' : s.number}
                        </StepCircle>
                        <StepLabel active={step === s.number}>{s.label}</StepLabel>
                    </Step>
                ))}
            </Steps>

            <FormSection>
                {step === 1 && (
                    <>
                        <FormGroup>
                            <Label>What is your main objective?</Label>
                            <TextArea
                                placeholder="e.g., Increase sales of our new summer collection by 20%"
                                value={formData.objective}
                                onChange={e => setFormData({ ...formData, objective: e.target.value })}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Who is your target audience?</Label>
                            <Input
                                placeholder="e.g., Young professionals aged 25-35 in urban areas"
                                value={formData.audience}
                                onChange={e => setFormData({ ...formData, audience: e.target.value })}
                            />
                        </FormGroup>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <FormGroup>
                                <Label>Budget ($)</Label>
                                <Input
                                    type="number"
                                    placeholder="5000"
                                    value={formData.budget}
                                    onChange={e => setFormData({ ...formData, budget: e.target.value })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Timeline</Label>
                                <Input
                                    placeholder="e.g., Q3 2024"
                                    value={formData.timeline}
                                    onChange={e => setFormData({ ...formData, timeline: e.target.value })}
                                />
                            </FormGroup>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h3 style={{ marginBottom: '20px', color: isDark ? '#f9fafb' : '#2d3748' }}>Select an AI-Generated Strategy</h3>
                        {aiSuggestions?.map(strategy => (
                            <AIResponseCard
                                key={strategy.id}
                                selected={formData.selectedStrategy?.id === strategy.id}
                                onClick={() => setFormData({ ...formData, selectedStrategy: strategy })}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <h4 style={{ margin: 0, color: isDark ? '#f9fafb' : '#2d3748' }}>{strategy.name}</h4>
                                    <span style={{ color: '#48bb78', fontWeight: '600' }}>ROI: {strategy.estimated_roi}</span>
                                </div>
                                <p style={{ color: isDark ? '#9ca3af' : '#718096', fontSize: '14px', marginBottom: '12px' }}>{strategy.description}</p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {strategy.channels.map(channel => (
                                        <span key={channel} style={{
                                            background: isDark ? '#374151' : '#edf2f7',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            color: isDark ? '#e5e7eb' : '#4a5568'
                                        }}>
                                            {channel}
                                        </span>
                                    ))}
                                </div>
                            </AIResponseCard>
                        ))}
                    </>
                )}

                {step === 3 && (
                    <>
                        <h3 style={{ marginBottom: '20px', color: isDark ? '#f9fafb' : '#2d3748' }}>Review Campaign Plan</h3>
                        <div style={{ background: isDark ? '#374151' : '#f7fafc', padding: '20px', borderRadius: '8px', border: isDark ? '1px solid #4b5563' : 'none' }}>
                            <div style={{ marginBottom: '15px' }}>
                                <strong style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Objective:</strong>
                                <p style={{ color: isDark ? '#d1d5db' : '#4a5568' }}>{formData.objective}</p>
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <strong style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Selected Strategy:</strong>
                                <p style={{ color: isDark ? '#d1d5db' : '#4a5568' }}>{formData.selectedStrategy?.name}</p>
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <strong style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Channels:</strong>
                                <p style={{ color: isDark ? '#d1d5db' : '#4a5568' }}>{formData.selectedStrategy?.channels.join(', ')}</p>
                            </div>
                            <div>
                                <strong style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Budget:</strong>
                                <p style={{ color: isDark ? '#d1d5db' : '#4a5568' }}>${formData.budget}</p>
                            </div>
                        </div>
                    </>
                )}

                {step === 4 && (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎉</div>
                        <h3 style={{ color: isDark ? '#f9fafb' : '#2d3748', marginBottom: '10px' }}>Campaign Launched Successfully!</h3>
                        <p style={{ color: isDark ? '#9ca3af' : '#718096', marginBottom: '30px' }}>
                            Our AI agents are now executing your campaign. You can monitor progress in the Command Center.
                        </p>
                        <Button primary onClick={() => navigate('/ai-command-center')}>
                            Go to Command Center
                        </Button>
                    </div>
                )}
            </FormSection>

            {step < 4 && (
                <ButtonGroup>
                    <Button onClick={handleBack} disabled={step === 1}>
                        Back
                    </Button>
                    <Button primary onClick={handleNext} disabled={loading}>
                        {loading ? 'Processing...' : step === 3 ? 'Launch Campaign' : 'Next'}
                    </Button>
                </ButtonGroup>
            )}
            </WizardContainer>
        </ThemeProvider>
    );
};

export default CampaignWizard;
